import { Printer } from 'lucide-react';
import { COMPANY } from '@jt/domain';
import { ContractDocument } from './ContractDocument';
import { DocumentCover, type CoverParty } from './DocumentCover';

/**
 * Take a copy away.
 *
 * ## Why printing rather than a generated file
 *
 * Every browser's print dialogue offers "Save as PDF", so this produces a real
 * PDF on every platform somebody here uses — including the Android a customer
 * reads their agreement on — without a PDF library on the server, a headless
 * browser, or a second rendering of the document that could drift from the one
 * on screen. What is printed is what was read, because it is the same markup.
 *
 * The alternative was server-side generation, which means a second layout to
 * maintain and a second chance to disagree with the first about what the
 * contract says. For a document whose whole value is being identical to what
 * was signed, one renderer is worth more than a download that skips a dialogue.
 *
 * ## Who gets it
 *
 * Everybody who can already read the contract. Sales, dispatch, an admin, and
 * the customer themselves — if a screen is allowed to show somebody the
 * agreement, it is allowed to let them keep a copy of it. Editing is a separate
 * permission and stays with the admin.
 */
export function PrintContractButton({ className = '' }: { className?: string }) {
  return (
    <button
      type="button"
      className={`btn-secondary print:hidden ${className}`}
      onClick={() => window.print()}
    >
      <Printer className="h-4 w-4" aria-hidden="true" />
      Download or print
    </button>
  );
}

/**
 * The contract as it should come out of a printer.
 *
 * Hidden on screen and revealed only for print, so the page can stay a
 * comfortable thing to read — scroll boxes, cards, a fixed header — while the
 * printed copy is a plain document with nothing cropped. A `max-h` scroll box
 * prints as a cropped box in every browser, which is the specific way a printed
 * contract goes wrong.
 */
export function PrintableContract({
  title,
  reference,
  company,
  body,
  signatures,
  counterpartyLabel = 'The customer',
  parties,
  issuedOn,
  standing,
}: {
  title: string;
  reference: string;
  company: string;
  body: string;
  signatures: { party: string; name: string; title?: string | null; signedAt: string }[];
  /**
   * Who the document is between, for the cover page.
   *
   * Optional, and the cover is omitted entirely without it. A cover that says
   * "Between: The provider ... and ..." with nothing after the "and" is worse
   * than no cover, so a caller that cannot name the other side does not get
   * one.
   */
  parties?: CoverParty[];
  /** When it was issued, printed on the cover as "Dated". */
  issuedOn?: string | null;
  /** A word on where it stands: "Executed", "Awaiting signature". */
  standing?: string | null;
  /**
   * What to call the other side on the printed signature block.
   *
   * A service agreement is signed by a customer and an offer of employment by
   * an employee, and the printed copy of each should say which. Defaulted, so
   * the customer contracts that were here first did not have to change.
   */
  counterpartyLabel?: string;
}) {
  return (
    /*
     * `contract-sheet` is what puts the company mark behind the words — see
     * index.css. It is on the printable copy rather than on the screen box
     * because this is the copy that leaves the building and gets kept,
     * photographed and forwarded when a building changes hands.
     */
    <div className="contract-sheet hidden print:block">
      {/*
        The mark behind the words, on every sheet.
        ------------------------------------------
        `position: fixed`, which is what makes it repeat: a browser paints a
        fixed element once per printed page, while the absolutely positioned
        version this replaces painted once for the whole document and therefore
        landed on one page out of five. A five-page agreement with the mark on
        page two looks like a photocopy of something else.
      */}
      <div className="contract-watermark" aria-hidden="true" />

      {/*
        A table, and not for layout's sake.
        -----------------------------------
        `thead` and `tfoot` are repeated on every printed page by every browser
        — it is the one mechanism for a running header that has worked
        everywhere for twenty years. That is what puts the logo, the company
        name and the reference at the top of page four, and the contact details
        at the bottom of it.

        The alternative, a fixed-position header, has to be pushed into the
        page margin by hand and lands differently in each browser. This is a
        document somebody prints once and keeps for five years; it is not the
        place to be clever.
      */}
      <table className="contract-paper">
        <thead>
          <tr>
            <th scope="col">
              <div className="contract-letterhead">
                {/*
                  Empty alt, deliberately. The company name is beside it, and a
                  screen reader announcing "GoSpeedNet logo" before it reads
                  "GoSpeedNet" twice for no gain.
                */}
                <img src="/brand/logo.png" alt="" />
                <div className="contract-letterhead-text">
                  <p className="contract-company">{COMPANY.legalName}</p>
                  <p className="contract-company-line">
                    {COMPANY.town}, {COMPANY.county}, {COMPANY.country} · {COMPANY.phoneE164}
                  </p>
                </div>
                <div className="contract-letterhead-doc">
                  <p className="contract-doc-title">{title}</p>
                  <p className="contract-doc-ref">{reference}</p>
                </div>
              </div>
            </th>
          </tr>
        </thead>

        {/*
          The details that make it a document rather than a print-out: who
          issued it, how to reach them, and which agreement this page belongs
          to — on every page, because pages get separated.
        */}
        <tfoot>
          <tr>
            <td>
              <div className="contract-footer">
                <span>
                  {COMPANY.legalName} · {COMPANY.phoneE164} · {COMPANY.email}
                </span>
                <span>
                  {reference} · {company}
                </span>
              </div>
            </td>
          </tr>
        </tfoot>

        <tbody>
          <tr>
            <td>
              {/*
                The cover, and then a page break.
                ---------------------------------
                Inside the table body rather than above the table, because the
                running header and footer have to appear on the cover too — a
                cover printed without the letterhead is a loose sheet that
                belongs to nothing, which is the opposite of what a cover is
                for.
              */}
              {parties && parties.length > 0 && (
                <DocumentCover
                  title={title}
                  reference={reference}
                  parties={parties}
                  issuedOn={issuedOn}
                  standing={standing}
                />
              )}

              <ContractDocument body={body} className="text-[11pt] leading-relaxed" />

      {signatures.length > 0 && (
        <section className="mt-8 break-inside-avoid border-t border-black/20 pt-4">
          <p className="font-heading text-sm">Signatures</p>
          <div className="mt-3 grid grid-cols-2 gap-6">
            {signatures.map((signature, index) => (
              <div key={index}>
                {/*
                  The name in a hand, over a rule, the way a signature block
                  looks on paper. It is the same string that was typed and
                  stored; the face is presentation, and the date beneath it is
                  the record.
                */}
                <p className="signature-ink text-xl leading-tight">{signature.name}</p>
                <p className="mt-1 border-t border-black/40 pt-1 text-xs">
                  {signature.party === 'company' ? 'For the provider' : counterpartyLabel}
                  {signature.title ? ` — ${signature.title}` : ''}
                  <br />
                  {new Date(signature.signedAt).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
